import { TFunction } from "i18next";
import { IoTcqLevel, IoTslaLevel, IoTslaStatus, NetworkScanState } from "../codegen/graphql";
import { getNetworkScanChipColors, NetworkScanChipColors } from "./networkScanConfig";

export type SlaChipColors = NetworkScanChipColors;

type Translate = TFunction | ((key: string) => string);

/** Neutral chip (INACTIVE, unknown status, ACTIVE without a level). */
const NEUTRAL_CHIP: SlaChipColors = { bg: "#F4F4F4", text: "#333333" };
/** The running 24h evaluation — the only blue chip in the app, so it reads as "in progress". */
const EVALUATION_CHIP: SlaChipColors = { bg: "#DCE8F6", text: "#0B4F8A" };

/** i18n keys of the status labels (chip text). */
const STATUS_LABELS: Record<string, string> = {
    [IoTslaStatus.ACTIVE]: "SLA active",
    [IoTslaStatus.INACTIVE]: "SLA inactive",
    [IoTslaStatus.EVALUATION]: "SLA evaluation",
    [IoTslaStatus.EVALUATION_FAILED]: "SLA evaluation failed",
};

/**
 * Statuses that call for an (again) on-site commissioning: INACTIVE (never
 * commissioned) and EVALUATION_FAILED (the 24h verification failed). Both
 * offer the "Start commissioning" button in the asset hero.
 */
export const isCommissioningSlaStatus = (status: string | null | undefined): boolean =>
    status === IoTslaStatus.INACTIVE || status === IoTslaStatus.EVALUATION_FAILED;

/**
 * i18n keys of the level labels — for both the SLA level (GOOD / DEGRADED /
 * OUT_OF_SLA) and the connectivity quality (GOOD / MEDIUM / BAD).
 */
const LEVEL_LABELS: Record<string, string> = {
    [IoTslaLevel.GOOD]: "Good",
    [IoTslaLevel.DEGRADED]: "Degraded",
    [IoTslaLevel.OUT_OF_SLA]: "Out of SLA",
    [IoTcqLevel.MEDIUM]: "Medium",
    [IoTcqLevel.BAD]: "Bad",
};

/** The level colours reuse the network scan traffic light (same green / yellow / red chips). */
const LEVEL_SCAN_STATE: Record<string, NetworkScanState> = {
    [IoTslaLevel.GOOD]: NetworkScanState.GREEN,
    [IoTslaLevel.DEGRADED]: NetworkScanState.ORANGE,
    [IoTslaLevel.OUT_OF_SLA]: NetworkScanState.RED,
    [IoTcqLevel.MEDIUM]: NetworkScanState.ORANGE,
    [IoTcqLevel.BAD]: NetworkScanState.RED,
};

const humanize = (value: string): string => {
    const humanized = value.replace(/_/g, " ").toLowerCase();
    return humanized.charAt(0).toUpperCase() + humanized.slice(1);
};

/**
 * Localized status label for the hero chip (IBM-1045 / IBM-1041). An enum
 * value the frontend does not know yet is humanized instead of leaking the
 * raw `SCREAMING_SNAKE` string.
 */
export const getSlaStatusLabel = (status: string | null | undefined, t: Translate): string => {
    if (!status) return "";
    const key = STATUS_LABELS[status];
    return key ? (t(key) as string) : humanize(status);
};

/** Localized SLA level or connectivity quality label; empty without a level. */
export const getSlaLevelLabel = (level: string | null | undefined, t: Translate): string => {
    if (!level) return "";
    const key = LEVEL_LABELS[level];
    return key ? (t(key) as string) : humanize(level);
};

/**
 * Chip colours: ACTIVE takes the colour of its level (traffic light), the
 * running evaluation is blue, a failed evaluation red, everything else
 * (INACTIVE, unknown) neutral grey.
 */
export const getSlaChipColors = (status: string | null | undefined, level: string | null | undefined): SlaChipColors => {
    if (status === IoTslaStatus.EVALUATION) return EVALUATION_CHIP;
    if (status === IoTslaStatus.EVALUATION_FAILED) return getNetworkScanChipColors(NetworkScanState.RED);
    if (status === IoTslaStatus.ACTIVE) {
        const scanState = level ? LEVEL_SCAN_STATE[level] : undefined;
        return scanState ? getNetworkScanChipColors(scanState) : NEUTRAL_CHIP;
    }
    return NEUTRAL_CHIP;
};

/**
 * Traffic-light chip for a level alone (SLA level or connectivity quality);
 * neutral grey for an unknown level.
 */
export const getSlaLevelChipColors = (level: string | null | undefined): SlaChipColors => {
    const scanState = level ? LEVEL_SCAN_STATE[level] : undefined;
    return scanState ? getNetworkScanChipColors(scanState) : NEUTRAL_CHIP;
};

/**
 * One-line hint next to the chip: what the status means for the user. The
 * INACTIVE / EVALUATION_FAILED hints assume the commissioning is still open — callers swap them for
 * the "submitted" line while a recent commissioning event is pending.
 */
export const getSlaStatusHint = (status: string | null | undefined, level: string | null | undefined, t: Translate): string => {
    switch (status) {
        case IoTslaStatus.INACTIVE:
            return t("Commissioning required") as string;
        case IoTslaStatus.EVALUATION:
            return t("24h connectivity verification running") as string;
        case IoTslaStatus.EVALUATION_FAILED:
            return t("Connectivity verification failed · repeat commissioning") as string;
        case IoTslaStatus.ACTIVE:
            return level ? `${t("Level") as string}: ${getSlaLevelLabel(level, t)}` : "";
        default:
            return "";
    }
};
